import { LIGHTBOX_CONFIG, LightboxConfig, LightboxModule } from 'ng-gallery/lightbox';
import { registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import ptBr from '@angular/common/locales/pt';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule } from '@angular/platform-browser';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { FullCalendarModule } from '@fullcalendar/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ptBrLocale } from 'ngx-bootstrap/locale';
import { ClipboardModule } from 'ngx-clipboard';
import { provideEnvironmentNgxLoaderIndicator } from 'ngx-loader-indicator';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { NgxPermissionsModule } from 'ngx-permissions';
import * as Sentry from '@sentry/angular';

import { SplashScreenModule } from './_metronic/partials/layout/splash-screen/splash-screen.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpUtilsService, LayoutUtilsService } from './core/_base/crud';
import { AuthAdminService } from './core/auth/service/auth-admin.service';
import { AuthModule } from './modules/auth/auth.module';
import { CanDeactivateGuard } from './shared/crud/can-deactivate.guard';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { AuthService } from './core/auth/service/auth.service';
import { AppCompanyResolverGuard } from './app-company-resolver.guard';
import { Router } from '@angular/router';
import { AuthVoluntaryService } from './core/auth/service/auth-voluntary.service';
import { AuthCooperativeService } from './core/auth/service/auth-cooperative.service';
import { AuthRegionalService } from './core/auth/service/auth-regional.service';
import { QuillModule } from 'ngx-quill';
import { MaintenanceComponent } from './maintenance/maintenance.component';

export function CustomPaginator() {
  const customPaginatorIntl = new MatPaginatorIntl();

  customPaginatorIntl.itemsPerPageLabel = 'Itens por página:';
  customPaginatorIntl.nextPageLabel = 'Próxima página';
  customPaginatorIntl.previousPageLabel = 'Página anterior';
  customPaginatorIntl.lastPageLabel = 'Última página';
  customPaginatorIntl.firstPageLabel = 'Primeira página';
  customPaginatorIntl.getRangeLabel = getRangeLabel;

  return customPaginatorIntl;
}

const getRangeLabel = (page: number, pageSize: number, length: number) => {
  if (length === 0 || pageSize === 0) {
    return `0 de ${length}`;
  }
  length = Math.max(length, 0);
  const startIndex = page * pageSize;
  const endIndex =
    startIndex < length
      ? Math.min(startIndex + pageSize, length)
      : startIndex + pageSize;
  return `${startIndex + 1} - ${endIndex} de ${length}`;
};

registerLocaleData(ptBr);

defineLocale('pt-br', ptBrLocale);

@NgModule({
  declarations: [AppComponent, MaintenanceComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot(),
    HttpClientModule,
    NgxPermissionsModule.forRoot(),
    ClipboardModule,
    AppRoutingModule,
    InlineSVGModule.forRoot(),
    AuthModule.forRoot(),
    BsDatepickerModule.forRoot(),
    SplashScreenModule,
    NgSelectModule,
    FullCalendarModule,
    MatTooltipModule,
    MatSelectModule,
    LightboxModule,
    QuillModule.forRoot()
  ],
  providers: [
    AuthService,
    AuthAdminService,
    AuthVoluntaryService,
    AuthCooperativeService,
    AuthRegionalService,
    provideEnvironmentNgxMask(),
    provideAnimations(),
    provideEnvironmentNgxLoaderIndicator(),
    HttpUtilsService,
    LayoutUtilsService,
    CanDeactivateGuard,
    {
      provide: MatPaginatorIntl,
      useValue: CustomPaginator(),
    },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    {
      provide: LIGHTBOX_CONFIG,
      useValue: {
        keyboardShortcuts: true,
        exitAnimationTime: 1000,
        panelClass: 'fullscreen'
      } as LightboxConfig,
    },
    AppCompanyResolverGuard,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
